import React, { useState } from 'react'
import { SpinnerLoader } from '@assets/icons'
import { Button, CustomSelect, ErrorForm, HeaderPage, InputFile, InputForm } from '@components/index'
import { IOptions } from '@components/inputs/CustomSelect'
import { useYupValidation } from '@hooks/index'
import { CATEGORY, IRequestApartments } from '@interfaces/apartments/IApartments'
import { errorHandler } from '@services/index'
import ApartmentService from '@services/requests/apartmentService'
import apartmentSchema from '@yupSchemas/apartment/apartmentSchema'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { IArrayError } from '@components/errors/ErrorForm'
import axios from 'axios'
import UserManagementService from '@services/requests/userManagementService'
import { FiUpload } from 'react-icons/fi'
import { REAL_ESTATE_TYPE } from '@interfaces/realEstates/enums/realEstateType'

export default function FormApartments(): React.ReactElement {
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingFile, setIsLoadingFile] = useState(false)
    const [realEstatesOptions, setRealEstatesOptions] = useState<IOptions[]>([])
    const [usersOptions, setUsersOptions] = useState<IOptions[]>([])
    const [data, setData] = React.useState<IRequestApartments | undefined>()
    const [isLoadingDownload, setIsLoadingDownload] = React.useState(false)
    const [isButtonDisabled, setIsButtonDisabled] = useState(false)
    const params = useParams()

    const { id } = useParams<{ id: string }>()

    const category = [
        { title: 'Planta', value: CATEGORY.BLUE_PRINT },
        { title: 'Manual do Proprietário', value: CATEGORY.MANUAL }
    ]

    const { handleSubmit, control, reset, formState: { errors } } = useForm<IRequestApartments>({
        resolver: useYupValidation(apartmentSchema),
        defaultValues: {
            filesData: []
        },
    })

    const { fields: fieldsFilesData, append: appendFilesData, remove: removeFilesData } = useFieldArray({
        control,
        name: 'filesData',
    })

    React.useEffect(() => {
        getRealEstate()
        getUser()
        getUpdateApartment()
    }, [])

    async function getUpdateApartment(): Promise<void> {
        if (params.id) {
            try {
                setIsLoading(true)
                if (!params.id) throw new Error('Id é necessário!')
                const response = await ApartmentService.getApartmentById(parseInt(params.id))
                setData(response)
                reset({
                    ...response,
                    userId: response.userId,
                    number: response.number,
                    realEstateId: response.realEstateId
                })
                if (response.realEstateApartmentFiles && response.realEstateApartmentFiles.length > 0) {
                    response.realEstateApartmentFiles.forEach((fileData) => {
                        const file = {
                            id: fileData.id,
                            fileId: fileData.id,
                            name: getNameWithoutExtension(fileData.name),
                            files: fileData.file,
                            type: fileData.type,
                            originalName: fileData.name,
                            file: fileData.file,
                            fileKey: fileData.fileKey
                        }
                        appendFilesData(file)
                    })
                }
            } catch (err) {
                toast(errorHandler(err).message, { type: 'error' })
                navigate('/apartamentos')
            } finally {
                setIsLoading(false)
            }
        }
    }

    async function getRealEstate(): Promise<void> {
        try {
            const response = await ApartmentService.getRealEstateSummary([REAL_ESTATE_TYPE.RELEASE, REAL_ESTATE_TYPE.VENTURE])
            setRealEstatesOptions(
                response.map((response) => ({
                    title: response.name,
                    value: response.id.toString()
                }))
            )
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        }
    }

    async function getUser(): Promise<void> {
        try {
            const response = await UserManagementService.getUsersSummary()
            setUsersOptions(
                response.map((user) => ({
                    title: user.name,
                    value: user.id
                }))
            )
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        }
    }

    async function submit(values: IRequestApartments): Promise<void> {
        const formData = new FormData()
        formData.append('number', values.number)
        formData.append('realEstateId', values.realEstateId)
        formData.append('userId', values.userId)
        if (values.filesData && values.filesData.length > 0) {
            const newFiles = []
            const existingFiles = []

            for (const file of values.filesData) {
                if (file?.id) {
                    // Arquivo existente, adiciona na lista de arquivos existentes
                    existingFiles.push(file)
                } else {
                    // Arquivo novo, adiciona no FormData
                    formData.append('files', file.file)

                    // Cria um novo objeto de arquivo para adicionar em newFiles
                    newFiles.push({
                        name: file.originalName,
                        type: file.type,
                        originalName: file.originalName,
                    })
                }
            }
            // Adiciona os arquivos existentes na lista newFiles, removendo o fileId e fileKey se existir
            const updatedNewFiles = existingFiles.map((file) => {
                const { fileId, fileKey, ...rest } = file
                { fileId }
                { fileKey }
                return rest
            })

            newFiles.push(...updatedNewFiles)

            formData.append('filesData', JSON.stringify(newFiles))
        }

        setIsLoading(true)
        try {
            if (data?.id) {
                await ApartmentService.update(data?.id, formData)
                toast('Apartamento salvo com sucesso!', { type: 'success' })
            } else {
                await ApartmentService.postApartment(formData)
                toast('Apartamento salvo com sucesso!', { type: 'success' })
            }
            navigate('/apartamentos')
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    function handleUploadFile(file: File): void {
        setIsLoadingFile(true)
        const fileName = getNameWithoutExtension(file.name)
        appendFilesData({ name: fileName, file: file, type: '', originalName: file.name, fileKey: '' })
        setIsLoadingFile(false)
    }

    function handleDeleteFile(index: number): void {
        removeFilesData(index)
    }

    function getNameWithoutExtension(nameFile: string): string {
        const pointIndex = nameFile.lastIndexOf('.')
        if (pointIndex !== -1) {
            return nameFile.substring(0, pointIndex)
        }
        return nameFile
    }

    async function fileDownload(fileId: number, fileKey: string): Promise<void> {
        try {
            setIsLoadingDownload(true)
            setIsButtonDisabled(true)
            const apartmentUrl = await ApartmentService.getFileKey(fileId)
            const response = await axios({
                method: 'GET',
                url: apartmentUrl,
                responseType: 'blob',
            })
            const url = window.URL.createObjectURL(response.data)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', fileKey)
            link.setAttribute('target', '_blank')
            document.body.appendChild(link)
            link.click()
            link.remove()
            toast('Download realizado com sucesso!', { type: 'success' })
        } catch (error) {
            toast(errorHandler(error).message, { type: 'error' })
        } finally {
            setIsLoadingDownload(false)
            setIsButtonDisabled(false)
        }
    }

    return (
        <div className="card">
            <div className="form-apartments">
                {isLoading ? (
                    <div className="loader-page">
                        <SpinnerLoader />
                    </div>
                ) : (
                    <></>
                )}
                <HeaderPage title={id ? 'Editar Apartamento' : 'Novo Apartamento'} />
                <form className="keeping__inputs" onSubmit={handleSubmit(submit)}>
                    <div className="wrapper-apartments">
                        <Controller
                            name="realEstateId"
                            control={control}
                            defaultValue={data?.realEstateId ?? 'none'}
                            render={({ field: { value, onChange } }) => {
                                return (
                                    <CustomSelect
                                        label="Imóvel"
                                        placeholder="Selecione o imóvel"
                                        name="realEstateId"
                                        value={value}
                                        defaultValue={value}
                                        onChange={onChange}
                                        options={realEstatesOptions}
                                        isFirstNoneDefault
                                    />
                                )
                            }}
                        />
                        <ErrorForm error={errors.realEstateId?.message} />
                        <Controller
                            name="number"
                            control={control}
                            defaultValue={data?.number ?? ''}
                            render={({ field: { value, onChange } }) => {
                                return (
                                    <InputForm
                                        label="Número"
                                        name="number"
                                        value={value}
                                        onChange={onChange}
                                        size='small'
                                        inputProps={{ maxLength: 8 }}
                                    />
                                )
                            }}
                        />
                        <ErrorForm error={errors.number?.message} />
                        <Controller
                            name="userId"
                            control={control}
                            defaultValue={data?.userId ?? 'none'}
                            render={({ field: { value, onChange } }) => (
                                <CustomSelect
                                    label="Proprietário"
                                    name="userId"
                                    value={value}
                                    onChange={onChange}
                                    options={usersOptions}
                                    isFirstNoneDefault
                                />
                            )}
                        />
                        <ErrorForm error={errors.userId?.message} />
                    </div>
                    <h4>Arquivos</h4>
                    <div className="wrapper-files">
                        <table className="table-files">
                            {fieldsFilesData.length > 0 && (
                                <tr className="box-options">
                                    <th>
                                        <h5>Nome do Arquivo</h5>
                                    </th>
                                    <th>
                                        <h5>Categoria</h5>
                                    </th>
                                    <th className='action'>
                                        <h5>Ação</h5>
                                    </th>
                                </tr>
                            )}
                            {fieldsFilesData.map((file, index) => (
                                <tr key={file.id}>
                                    <th>
                                        <Controller
                                            name={`filesData.${index}.name`}
                                            control={control}
                                            defaultValue={data?.filesData?.[index]?.name ?? ''}
                                            render={({ field: { value, onChange } }) => (
                                                <InputForm
                                                    label="Nome do arquivo"
                                                    placeholder="Nome do arquivo"
                                                    name={`filesData.${index}.name`}
                                                    value={value}
                                                    onChange={onChange}
                                                    size="small"
                                                    style={{ width: '15rem' }}
                                                />
                                            )}
                                        />
                                        <ErrorForm error={(errors as IArrayError)[`filesData[${index}].name`]?.message} />
                                    </th>
                                    <th>
                                        <Controller
                                            name={`filesData.${index}.type`}
                                            control={control}
                                            defaultValue={file.type}
                                            render={({ field: { value, onChange } }) => (
                                                <CustomSelect
                                                    label="Categoria"
                                                    placeholder="Selecione a categoria"
                                                    name={`realApartmentFiles[${index}].type`}
                                                    value={value}
                                                    onChange={onChange}
                                                    style={{ width: '15rem' }}
                                                    options={category}
                                                />
                                            )}
                                        />
                                        <ErrorForm error={(errors as IArrayError)[`filesData[${index}].type`]?.message} />
                                    </th>
                                    <th className='action'>
                                        {!file.file && (
                                            <button
                                                type='button'
                                                className="link-button"
                                                style={{ marginRight: '0.5rem' }}
                                                title="Download"
                                                disabled={isButtonDisabled}
                                                onClick={() => { if (file.fileId) { fileDownload(file.fileId, file.fileKey) } }}>
                                                Download
                                                {isLoadingDownload ? (
                                                    <div className="loader-page">
                                                        <SpinnerLoader />
                                                    </div>
                                                ) : (
                                                    <></>
                                                )}
                                            </button>
                                        )}
                                        <button
                                            className="link-button"
                                            style={{ marginRight: '0.5rem' }}
                                            title="Excluir"
                                            onClick={() => handleDeleteFile(index)}
                                        >
                                            Excluir
                                        </button>
                                    </th>
                                </tr>
                            ))}
                        </table>
                        <div className="box-add-file-button">
                            <label className="button-download">
                                <InputFile
                                    name="files"
                                    acceptFiles='image/jpeg, image/png, application/pdf'
                                    multiple
                                    isLoadingFile={isLoadingFile}
                                    onChange={(file) => { if (file) handleUploadFile(file) }}
                                />
                                <FiUpload size={35} />
                                <p>
                                    Selecione um novo arquivo aqui
                                </p>
                            </label>
                            <ErrorForm error={errors.filesData?.message} />
                        </div>
                    </div>
                    <div className="box-button-apartments">
                        <Button type={'button'} placeholder="Cancelar" customClasses="button-secondary" onClick={() => navigate('/apartamentos/')} />
                        <Button isLoading={isLoading} type={'submit'} placeholder="Salvar" />
                    </div>
                </form>
            </div >
        </div >
    )
}
